<template>
  <v-card class="marketing-offer-card" :to="toUrl">
    <div class="card-image" :style="{ backgroundImage: 'url(' + showcaseImageHelper(image) + ')' }">
      <div v-show="status_text" class="h-text-4 home-card-status">{{ status_text }}</div>
    </div>

    <div class="card-content">
      <div class="title-container h-heading-h4">
        <span>{{ title }}</span>
      </div>

      <div class="marketing-offer-card-badge">
        <div v-if="useGiftIcon" class="hello-gift-badge">
          <v-icon color="ultra-light" class="hello-gift-badge-icon">mdi-wallet-giftcard</v-icon>
        </div>
        <div v-else class="hello-bonus-badge">{{ text }}</div>

        <div class="hello-category-label">
          <div class="h-text-4 region-text">{{ region }}</div>
          <div class="h-text-4 category-text">{{ sub_region }}</div>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script>
import { showcaseImageHelper } from '@/composables/image-helper';
import { mapGetters } from 'vuex';

export default {
  setup() {
    return { showcaseImageHelper };
  },
  name: 'marketing-offer-card',
  props: {
    offer: {
      type: Object,
      require: true,
    },
  },
  computed: {
    ...mapGetters('ui', ['currentLocale']),
    ...mapGetters('space', ['publicSpaceByPartnerIdSlugGetter']),
    useGiftIcon() {
      if (this.offer.current_promotion?.status === 'available') {
        return false;
      }
      if (this.offer.features?.promotion_only) {
        return false;
      }
      return this.offer.type !== 'bonus';
    },
    text() {
      return `+${this.offer.current_promotion?.incentive?.bonus_display || this.offer?.incentive?.bonus_display}%`;
    },
    toUrl() {
      return `/${this.publicSpaceByPartnerIdSlugGetter(this.offer.partner_id)}/offers/${this.offer.id}`;
    },
    sub_region() {
      if (!this.offer.is_single_publisher) {
        return this.$t('components.marketing_offer_card.multi_publisher');
      }
      if (this.offer.store_count > 0) {
        return this.$tc('components.marketing_offer_card.store_count', this.offer.store_count, { store_count: this.offer.store_count });
      }
      return undefined;
    },
    title() {
      return this.offer?.offer_branding?.label?.[this.currentLocale];
    },
    image() {
      return this.offer?.offer_branding?.images?.showcases?.[this.currentLocale]?.[0];
    },
    region() {
      return this.offer?.offer_branding?.region_label?.[this.currentLocale];
    },
    status_text() {
      if (this.offer?.status === 'coming_soon') {
        return this.$t('components.marketing_offer_card.coming_soon', { start_date: this.$options.filters.formatShortDateWithHour(this.offer?.availability?.start_date) });
      }

      if ((this.offer.current_promotion?.status === 'sold_out' || this.offer?.status === 'closed' || this.offer.current_promotion?.status === 'closed') && this.offer.features?.promotion_only) {
        return this.$t('components.marketing_offer_card.sold_out');
      }

      if (this.offer.current_promotion?.status === 'sold_out') {
        return this.$t('components.marketing_offer_card.sold_out_promo', { bonus_display: this.offer.current_promotion?.incentive?.bonus_display });
      }

      if (this.offer.current_promotion?.status === 'coming_soon') {
        return this.$t('components.marketing_offer_card.coming_soon_promo', {
          bonus_display: this.offer.current_promotion?.incentive?.bonus_display,
          start_date: this.$options.filters.formatShortDateWithHour(this.offer.current_promotion?.availability?.start_date),
        });
      }

      if (this.offer?.status === 'sold_out' || this.offer?.status === 'closed') {
        return this.$t('components.marketing_offer_card.sold_out');
      }

      return undefined;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/scss/utilities/mixin.scss';

.marketing-offer-card {
  --warning: #dc502d;
  border-radius: 20px !important;
  box-shadow: none;
  border: 2px solid #e6eef8;
  background-color: #e6eef8 !important;

  &:hover {
    cursor: pointer;
    opacity: 1 !important;
    transition: 0.25s ease-in-out;
    box-shadow: 0px 4px 20px 8px rgba(0, 0, 0, 0.12) !important;
  }

  &:not(:hover) {
    cursor: default;
    transition: 0.25s ease-in-out;
    box-shadow: none !important;
  }

  --home-card-badge: 36px;

  @media ($sm-min) {
    --home-card-badge: 28px;
  }
}

.card-image {
  position: relative;

  // This is the future
  // aspect-ratio: 2 / 1;

  // Remove this padding-top property when you are no longer in the past.
  padding-top: 48.554913%;

  background-position: center center;
  background-size: cover;
  background-repeat: no-repeat;
}

.card-content {
  display: block;
  padding: 12px;
  background-color: white;
  @media ($md-min) {
    padding: 16px;
  }
}

.title-container {
  display: flex;
  align-items: center;
  height: calc(1.3 * var(--font-size-h4) * 2);
  font-weight: 500;
  color: var(--color-branding);
  font-size: 20px;
  @media ($sm-min) {
    font-size: 18px;
  }
  @media ($md-min) {
    font-size: 22px;
  }
  & > * {
    // Cut after 2 line with ...
    @include cut-after-x-line(2);
  }
}

.marketing-offer-card-badge {
  display: flex;
  align-items: center;
  margin-top: 12px;
}

.hello-category-label {
  color: var(--color-text-primary);
  font-weight: 500;
  line-height: 1.5;
  min-height: 36px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  @media ($md-min) {
    min-height: 42px;
  }
}

.hello-gift-badge {
  display: flex;
  justify-content: center;
  background-color: var(--color-branding);
  padding: 0 6.68px;
  margin-right: 12px;
  text-align: center;
  line-height: 0.9;
  width: var(--home-card-badge);
  height: var(--home-card-badge);
  @media ($md-min) {
    height: 34px;
    width: 34px;
  }
}

.hello-gift-badge-icon {
  font-size: 20px;
  @media ($sm-min) {
    font-size: 16px;
  }
}

.hello-bonus-badge {
  display: flex;
  background-color: var(--warning);
  font-weight: bold;
  font-size: 16px;
  line-height: 0.9;
  text-align: center;
  color: var(--color-ultra-light);
  padding: 0 6px;
  margin-right: 15px;
  height: var(--home-card-badge);
  align-items: center;

  @media ($sm-min) {
    font-size: 12px;
  }

  @media ($md-min) {
    font-size: 15.4px;
    height: 34px;
  }
}

.home-card-status {
  bottom: -1px;
  left: 0;
  position: absolute;
  background-color: var(--warning);
  color: var(--color-ultra-light);
  font-weight: 600;
  line-height: 1.5;
  padding: 6px;
}

.category-text,
.region-text {
  font-size: 12px;
  @media ($md-min) {
    font-size: 12px;
  }
  @media ($lg-min) {
    font-size: 14px;
  }
  @include cut-after-x-line(1);
}
</style>
